/**
 * Private Company 409A Calculator
 *
 * Default calculator for most private company clients.
 * Calculates in-the-money value: shares * (sharePrice - strikePrice)
 *
 * This is the standard valuation method used by most clients (lines 6032-6035 in index.js):
 *   latestValue[key] = Number(userDataShares[key].owned) * (Number(latest[0].sharePrice) - strikePrice);
 *
 * Supports optional floor at zero for underwater positions (isSEQ behavior).
 * Outputs both total value (all owned) and vested value for display flexibility.
 */

const PrivateCompany409ACalculator = {
  methodId: 'private_company_409a',
  displayName: 'Private Company (In-the-Money Value)',
  requiredDataSources: ['valuations', 'strikePrice'],

  // Graph configuration - stacked area chart shows value by share class over time
  graphConfig: {
    recommendedType: 'time_series_stacked',
    timeSeriesStrategy: 'valuation_by_class',
    supportsTimeSeries: true
  },

  /**
   * Calculate in-the-money value
   * @param {Object} input
   * @param {Object} input.holdings - { owned, vested, sharePrice, strikePrice } by class
   * @param {string[]} input.shareClasses - Array of share class keys
   * @param {Object} input.options - { floorAtZero: boolean }
   * @returns {Object} CalculationResult
   */
  calculate: function(input) {
    const holdings = input.holdings;
    const shareClasses = input.shareClasses || Object.keys(holdings.owned || {});
    const options = input.options || {};
    const floorAtZero = options.floorAtZero || false;

    let totalValue = 0;       // Value based on all owned shares
    let vestedValue = 0;      // Value based on vested shares only
    const breakdown = [];

    shareClasses.forEach(function(key) {
      const owned = holdings.owned[key] || 0;
      const vested = holdings.vested?.[key] || owned;  // Default to owned if no vesting data
      const sharePrice = holdings.sharePrice[key] || 0;
      const strikePrice = holdings.strikePrice[key] || 0;

      // Calculate per-share value (price - strike)
      const perShareValue = sharePrice - strikePrice;

      // In-the-money value: qty * (price - strike)
      // This matches line 6034: Number(userDataShares[key].owned) * (Number(latest[0].sharePrice) - strikePrice)
      let value = owned * perShareValue;
      let classVestedValue = vested * perShareValue;

      // Optional: floor at zero for underwater positions (isSEQ behavior from line 6360)
      if (floorAtZero) {
        value = Math.max(0, value);
        classVestedValue = Math.max(0, classVestedValue);
      }

      totalValue += value;
      vestedValue += classVestedValue;
      const classUnvestedValue = value - classVestedValue;
      breakdown.push({
        classKey: key,
        className: key,
        value: value,
        vestedValue: classVestedValue,
        unvestedValue: classUnvestedValue,
        owned: owned,
        vested: vested,
        sharePrice: sharePrice,
        strikePrice: strikePrice
      });
    });

    const unvestedValue = totalValue - vestedValue;

    return {
      totalValue: totalValue,       // Total value based on all owned shares
      vestedValue: vestedValue,     // Value based on vested shares only (for graph display)
      unvestedValue: unvestedValue, // Unvested grant value (totalValue - vestedValue)
      breakdown: breakdown,
      fundMetrics: null,  // Not applicable for private company
      metadata: {
        methodUsed: this.methodId,
        calculationDate: new Date(),
        options: { floorAtZero: floorAtZero }
      }
    };
  }
};

export { PrivateCompany409ACalculator };
